import React from 'react';
import {
    AdminEventCreateDto
} from '../../../../types/admin.types';
import SearchDropdown, { SearchDropdownOption } from './SearchDropdown';
import AdminDateTimeInput from './AdminDateTimeInput';

interface EventFormFieldsProps {
    form: AdminEventCreateDto;
    onChange: (next: AdminEventCreateDto) => void;
    fieldClassName: string;
    actionsClassName: string;
    secondaryButtonClassName?: string;
    locationOptions: SearchDropdownOption[];
    onLocationSearchChange: (query: string) => void;
    onCreateVenueClick?: () => void;
    showCreateVenueButton?: boolean;
    idPrefix?: string;
    requiredName?: boolean;
    requiredDate?: boolean;
}

export const emptyEventForm: AdminEventCreateDto = {
    name: '',
    description: '',
    eventDate: '',
    eventUrl: '',
    thumbnailUrl: '',
    locationId: '',
    tags: [],
    hideFromFightCards: false
};

export const normalizeEventCreatePayload = (form: AdminEventCreateDto): AdminEventCreateDto => ({
    name: (form.name ?? '').trim(),
    description: form.description && form.description.trim().length > 0 ? form.description.trim() : null,
    eventDate: form.eventDate ?? '',
    eventUrl: form.eventUrl && form.eventUrl.trim().length > 0 ? form.eventUrl.trim() : null,
    thumbnailUrl: form.thumbnailUrl && form.thumbnailUrl.trim().length > 0 ? form.thumbnailUrl.trim() : null,
    locationId: form.locationId && form.locationId.trim().length > 0 ? form.locationId.trim() : null,
    tags: (form.tags ?? [])
        .map((tag) => (tag ?? '').trim())
        .filter((tag) => tag.length > 0),
    hideFromFightCards: form.hideFromFightCards ?? false
});

const EventFormFields = ({
    form,
    onChange,
    fieldClassName,
    actionsClassName,
    locationOptions,
    onLocationSearchChange,
    onCreateVenueClick,
    showCreateVenueButton = false,
    idPrefix = 'event',
    requiredName = true,
    requiredDate = true,
    secondaryButtonClassName
}: EventFormFieldsProps) => (
    <>
        <div className={fieldClassName}>
            <label htmlFor={`${idPrefix}-name`}>Name</label>
            <input
                id={`${idPrefix}-name`}
                value={form.name}
                onChange={(event) => onChange({ ...form, name: event.target.value })}
                maxLength={200}
                required={requiredName}
            />
        </div>
        <div className={fieldClassName}>
            <label htmlFor={`${idPrefix}-description`}>Description</label>
            <textarea
                id={`${idPrefix}-description`}
                value={form.description ?? ''}
                onChange={(event) => onChange({ ...form, description: event.target.value })}
                maxLength={2000}
            />
        </div>
        <div className={fieldClassName}>
            <label htmlFor={`${idPrefix}-date`}>Event Date (local)</label>
            <AdminDateTimeInput
                id={`${idPrefix}-date`}
                value={form.eventDate}
                includeTime
                onChange={(value) => onChange({ ...form, eventDate: value })}
                required={requiredDate}
            />
        </div>
        <div className={fieldClassName}>
            <label htmlFor={`${idPrefix}-location`}>Venue</label>
            <SearchDropdown
                id={`${idPrefix}-location`}
                value={form.locationId ?? ''}
                options={locationOptions}
                onChange={(value) => {
                    const nextValue = value || '';
                    onChange({ ...form, locationId: nextValue });
                    const selectedLocation = locationOptions.find((option) => option.value === nextValue);
                    onLocationSearchChange(selectedLocation?.label ?? '');
                }}
                onSearchChange={onLocationSearchChange}
                placeholder="Search venue"
                noResultsText="No venues found."
            />
            {showCreateVenueButton && onCreateVenueClick ? (
                <div className={actionsClassName}>
                    <button type="button" className={secondaryButtonClassName} onClick={onCreateVenueClick}>
                        Create Venue
                    </button>
                </div>
            ) : null}
        </div>
        <div className={fieldClassName}>
            <label htmlFor={`${idPrefix}-url`}>Event URL</label>
            <input
                id={`${idPrefix}-url`}
                value={form.eventUrl ?? ''}
                onChange={(event) => onChange({ ...form, eventUrl: event.target.value })}
                maxLength={2000}
            />
        </div>
        <div className={fieldClassName}>
            <label htmlFor={`${idPrefix}-hide-fight-cards`}>
                <input
                    id={`${idPrefix}-hide-fight-cards`}
                    type="checkbox"
                    checked={form.hideFromFightCards ?? false}
                    onChange={(event) => onChange({ ...form, hideFromFightCards: event.target.checked })}
                />
                {' '}Hide event from Fight Cards
            </label>
        </div>
    </>
);

export default EventFormFields;
